import {
  ArrowRight,
  BookOpen,
  Check,
  Code2,
  GitCommit,
  Map,
  Play,
} from "lucide-react";
import { Link, type MetaFunction, useLoaderData } from "react-router";

import { formatPrice } from "@/site/billing/config";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";

import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import { LearningMap } from "./learning-map";
import { buildRepositoryCommitHref } from "./navigation";
import { codeFontStyle } from "./source-browser";
import type { MarketingLoaderResponse } from "./types";

export const marketingMeta: MetaFunction = () => {
  return [
    { title: publicEnv.siteTitle },
    {
      name: "description",
      content:
        "Learn how a production Makefile evolves through guided lessons, GNU Make concepts, and complete repository history.",
    },
  ];
};

export function MarketingPage() {
  const {
    appendixCount,
    canAccessPaidLessons,
    conceptCount,
    dbUser,
    firstFreeLessonCommit,
    hasLockedPricing,
    lessonCount,
    moduleCount,
    pricing,
    repositoryCommitCount,
  } = useLoaderData<MarketingLoaderResponse>();
  const primaryHref = canAccessPaidLessons
    ? "/course"
    : `/course/${firstFreeLessonCommit}`;
  const primaryLabel = canAccessPaidLessons
    ? "Continue the course"
    : "Start learning free";
  const appendixCountLabel = `${appendixCount} ${
    appendixCount === 1 ? "appendix" : "appendices"
  }`;

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-14 pb-20 sm:w-[calc(100%-3rem)] sm:gap-20 sm:pb-28">
        <header className="grid max-w-4xl gap-8 pt-12 sm:pt-16 lg:pt-20">
          <Badge className="w-fit font-mono shadow-xs" variant="secondary">
            <Code2 />
            Production Make, explained
          </Badge>
          <div className="grid gap-5">
            <h1 className="max-w-4xl text-4xl leading-tight font-semibold text-balance sm:text-6xl">
              Understand the decisions behind a production Makefile
            </h1>
            <p className="text-muted-foreground max-w-3xl text-lg leading-8 text-pretty sm:text-xl">
              Follow a real repository from its first target to a managed
              toolchain. Learn the Make model, practice the commands, and
              inspect every source snapshot along the way.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button asChild className="w-full justify-center sm:w-auto">
              <Link to={primaryHref}>
                <BookOpen />
                {primaryLabel}
              </Link>
            </Button>
            <Button
              asChild
              className="w-full justify-center sm:w-auto"
              variant="outline"
            >
              <Link to="/course#course-path">Browse the curriculum</Link>
            </Button>
          </div>
          <div className="text-muted-foreground flex flex-wrap gap-x-5 gap-y-2 font-mono text-xs">
            <span>{moduleCount} modules</span>
            <span>{appendixCountLabel}</span>
            <span>{lessonCount} lessons</span>
            <span>{conceptCount} concepts</span>
            <span>{repositoryCommitCount} commits</span>
          </div>
          <p className="text-muted-foreground max-w-3xl text-sm leading-6 text-pretty">
            Makefile FYI is a growing course. As the repository evolves, commits
            that introduce a useful production decision can become new lessons.
            The counts above show what is available today.
          </p>
        </header>

        <section
          aria-labelledby="marketing-proof-title"
          className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)] lg:items-center"
        >
          <div className="bg-card overflow-hidden rounded-md border shadow-sm">
            <div className="bg-muted/30 flex items-center justify-between gap-3 border-b px-4 py-3">
              <span className="font-mono text-xs">Makefile</span>
              <span className="text-muted-foreground font-mono text-xs">
                commit {firstFreeLessonCommit}
              </span>
            </div>
            <pre
              className="bg-background overflow-x-auto px-4 py-5 text-sm leading-7"
              style={codeFontStyle}
            >
              <code>{`.PHONY: all\nall:`}</code>
            </pre>
          </div>

          <div className="grid gap-5">
            <div className="grid gap-2">
              <h2
                className="text-2xl leading-tight font-semibold"
                id="marketing-proof-title"
              >
                One change, three ways to understand it
              </h2>
              <p className="text-muted-foreground leading-7">
                Learn why the default goal matters, practice the behavior, then
                inspect the exact repository state behind the explanation.
              </p>
            </div>
            <ul className="grid gap-2 text-sm">
              <li className="flex items-center gap-2">
                <BookOpen className="text-muted-foreground size-4" />
                Follow the guided lesson
              </li>
              <li className="flex items-center gap-2">
                <Play className="text-muted-foreground size-4" />
                Replay the terminal commands
              </li>
              <li className="flex items-center gap-2">
                <GitCommit className="text-muted-foreground size-4" />
                Browse the complete source snapshot
              </li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="sm" variant="outline">
                <Link to={`/course/${firstFreeLessonCommit}`}>
                  Open the free lesson
                </Link>
              </Button>
              <Button asChild size="sm" variant="ghost">
                <Link
                  to={buildRepositoryCommitHref({
                    commit: firstFreeLessonCommit,
                    filePath: "Makefile",
                    lineNumbers: [14, 15],
                  })}
                >
                  Inspect the commit
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <LearningMap />

        <section
          aria-labelledby="marketing-curriculum-title"
          className="border-border/70 grid gap-7 border-y py-9 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:py-11"
        >
          <div className="grid max-w-3xl gap-3">
            <h2
              className="text-3xl leading-tight font-semibold"
              id="marketing-curriculum-title"
            >
              A focused path through real engineering decisions
            </h2>
            <p className="text-muted-foreground leading-7">
              The course organizes {lessonCount} milestone lessons into{" "}
              {moduleCount} core modules, followed by {appendixCountLabel}.
              Every core module and appendix begins with a free foundation, then
              connects the explanation to concepts and repository history.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/course#course-path">
              View all lessons
              <ArrowRight />
            </Link>
          </Button>
        </section>

        <section
          aria-labelledby="marketing-guide-title"
          className="grid gap-6 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center"
        >
          <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-12 items-center justify-center rounded-md shadow-xs ring-1">
            <Map className="size-5" />
          </span>
          <div className="grid gap-2">
            <h2 className="text-2xl font-semibold" id="marketing-guide-title">
              New to the learning system?
            </h2>
            <p className="text-muted-foreground max-w-2xl leading-7">
              See how lessons, concept references, source links, terminal
              replays, access, and local progress work together.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/guide">See how it works</Link>
          </Button>
        </section>

        <section
          aria-labelledby="marketing-pricing-title"
          className="bg-card grid gap-7 rounded-md border p-6 shadow-sm sm:p-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
        >
          <div className="grid max-w-2xl gap-3">
            <h2
              className="text-3xl leading-tight font-semibold"
              id="marketing-pricing-title"
            >
              Start free, then unlock everything
            </h2>
            <p className="text-muted-foreground leading-7">
              Full access includes every lesson, concept, repository snapshot,
              and replay. Pay {formatPrice(pricing.lifetimePriceCents)} once or
              start with a {pricing.passDurationDays}-day pass for{" "}
              {formatPrice(pricing.unitPriceCents)}. Every pass counts toward
              lifetime access.
            </p>
            <p className="text-muted-foreground text-sm leading-6">
              {hasLockedPricing ? (
                <>
                  Your first successful purchase locked this{" "}
                  {formatPrice(pricing.lifetimePriceCents)} lifetime target at{" "}
                  {formatPrice(pricing.unitPriceCents)} per unit. Later public
                  price changes do not affect it.
                </>
              ) : (
                <>
                  The public lifetime price may increase as the course grows.
                  Your first successful purchase—not account creation—locks the
                  unit price and lifetime target offered at that time.
                </>
              )}
            </p>
            <ul className="grid gap-2 text-sm sm:grid-cols-2">
              <li className="flex items-center gap-2">
                <Check className="text-success size-4" />
                Free foundation in every section
              </li>
              <li className="flex items-center gap-2">
                <Check className="text-success size-4" />
                No automatic renewal
              </li>
            </ul>
          </div>
          <Button asChild>
            <Link to="/pricing">
              View pricing
              <ArrowRight />
            </Link>
          </Button>
        </section>

        <section className="grid justify-items-center gap-5 text-center">
          <div className="grid max-w-2xl gap-2">
            <h2 className="text-3xl font-semibold">Start with the source</h2>
            <p className="text-muted-foreground leading-7">
              Open the first lesson for free and see whether this way of
              learning Make fits how you work.
            </p>
          </div>
          <Button asChild>
            <Link to={primaryHref}>
              {primaryLabel}
              <ArrowRight />
            </Link>
          </Button>
        </section>
      </main>

      <CourseFooter />
    </CourseShell>
  );
}
